/**
 * AI Recruit - Proctored Interview API Service
 * Interviewer-facing (authenticated) config/submissions/scorecard calls.
 * The candidate-facing public flow lives in proctoredInterviewPublicApi.ts —
 * kept separate so it never goes through the auth-token interceptor.
 */

import { apiV2 } from './api'

export interface RubricCriterion {
  name: string
  description?: string
  // This criterion's share of its skill's 100 points. The criteria within one
  // skill must total 100 — the backend rejects a save that doesn't.
  // Optional: configs saved before weighting existed have none, and score by
  // an equal split instead.
  weight?: number | null
}

export interface QuestionMixEntry {
  question_type_id: string
  question_subtype_id?: string | null
  // Complexity level these questions are pitched at (see complexityLevelApi).
  complexity_level_id?: string | null
  count: number
}

export interface SkillRubric {
  skill_name: string
  weight: number
  rubric_criteria: RubricCriterion[]
  // Which question types/subtypes (and how many of each) assess this skill —
  // configured per skill rather than globally.
  question_mix: QuestionMixEntry[]
}

export interface TestCase {
  input: string
  expected_output: string
  description?: string
}

export interface ProctoredQuestion {
  id: string
  question_type_id: string
  question_subtype_id?: string | null
  complexity_level_id?: string | null
  skill_name: string
  rubric_criteria_covered: string[]
  question_text: string
  guidance_notes?: string | null
  test_cases: TestCase[]
  duration_minutes: number
  display_order: number
}

export interface ProctoredInterviewConfig {
  id: string
  job_id: string
  status: 'draft' | 'active'
  version: number
  jd_context: Record<string, any>
  context_extracted_at?: string | null
  skills_rubrics: SkillRubric[]
  total_duration_minutes?: number | null
  questions_generated_at?: string | null
  created_at: string
  updated_at: string
  created_by_name?: string | null
  updated_by_name?: string | null
  questions: ProctoredQuestion[]
}

export interface AuditLogEntry {
  action: string
  version: number
  changed_by_name?: string | null
  details: Record<string, any>
  created_at: string
}

export interface SubmissionSession {
  job_match_id: string
  resume_id: string
  candidate_name?: string | null
  candidate_email?: string | null
  session_id: string
  // 'incomplete' = the session stopped without the candidate submitting and
  // without the 3-violation auto-submit, and the sweeper claimed it.
  status: 'pending' | 'verified' | 'in_progress' | 'completed' | 'incomplete'
  public_url: string
  overall_score?: number | null
  recommendation?: string | null
  completed_at?: string | null
  auto_submitted_due_to_violations: boolean
  violation_summary?: string | null
  invite_email_sent_at?: string | null

  // Attempt history. Everything above describes the LATEST attempt.
  // 1 for every candidate who was never interrupted — the normal case, where
  // the row shows no attempt-related controls at all.
  attempt_number: number
  // Exactly when the recruiter may generate a new link: latest attempt is
  // incomplete and the cap has not been reached.
  can_reissue: boolean
  // Session the "View Result" button opens — the most recent attempt that
  // finished (completed or incomplete). Null when none has, in which case no
  // button is rendered.
  result_session_id?: string | null
}

export interface TestCaseResult {
  description?: string
  passed: boolean
  explanation?: string
}

export interface RunHistoryEntry {
  action: 'run' | 'run_tests'
  at: string
  code: string
  language?: string
  stdout?: string
  stderr?: string
  exit_code?: number | null
  results?: TestCaseResult[]
}

export interface ScorecardQuestion {
  question_id: string
  skill_name: string
  question_type_name: string
  question_subtype_name?: string | null
  question_text: string
  duration_minutes: number
  test_cases: TestCase[]
  answer_text?: string | null
  answer_score?: number | null
  answer_summary?: string | null
  answer_rubric_scores: SkillScoreCriterion[]
  answer_test_case_results: TestCaseResult[]
  answer_whiteboard_image?: string | null
  time_spent_seconds?: number | null
  display_order: number
  // Coding questions only: every Run/Run Test Cases attempt, oldest first —
  // lets the recruiter expand and see exactly what the candidate did.
  answer_run_history: RunHistoryEntry[]
}

export interface SkillScoreCriterion {
  criterion: string
  // Raw 0-100 judgement of this criterion on its own terms.
  score: number
  feedback?: string
  // Set by the scoring rollup: the criterion's share of the skill's 100
  // points, and how many of those points this answer earned
  // (earned = score/100 * weight). Rendered as "earned/weight". Both are
  // null when the criterion was never assessed, and absent entirely on
  // scorecards produced before weighting existed.
  weight?: number | null
  earned?: number | null
  // False when no question in the bank covered this criterion, so the
  // candidate had no opportunity to demonstrate it. Those are excluded from
  // the skill score rather than counted as zero.
  assessed?: boolean
}

export interface SkillScore {
  // 0-100, computed only over the criteria that were actually assessed.
  score: number
  criteria: SkillScoreCriterion[]
  // How many of this skill's criteria a question actually covered. When
  // these differ, the score rests on partial evidence and the scorecard says
  // so. Absent on scorecards produced before this existed.
  criteria_assessed?: number
  criteria_total?: number
}

export interface IntegrityEvent {
  type: string
  details: Record<string, any>
  detected_at: string
}

export interface PlagiarismMatch {
  question_id: string
  matched_candidate_name: string
  // 0.0-1.0 Jaccard similarity score — render as a rounded whole-number
  // percentage (see services/plagiarism_detection.py).
  similarity_score: number
}

export interface ProctoringInfo {
  ipv4?: string | null
  ipv6?: string | null
  location?: { lat: number; lng: number; accuracy_meters?: number | null; place_name?: string | null } | null
  // Approximate, IP-based location — a separate signal from `location`
  // (GPS), computed independently and shown as its own scorecard field.
  ip_location?: { city?: string | null; region?: string | null; country?: string | null; place_name?: string | null } | null
  camera_mic_verified: boolean
  camera_snapshot?: string | null
  extra_monitor_detected?: boolean | null
  declaration_confirmed_at?: string | null
  fullscreen_violations: number
  auto_submitted_due_to_violations: boolean
  integrity_events: IntegrityEvent[]
  recording_url?: string | null
}

export interface Scorecard {
  session_id: string
  status: string
  candidate_name?: string | null
  candidate_email?: string | null
  job_title?: string | null
  overall_score?: number | null
  skill_scores: Record<string, SkillScore>
  recommendation?: string | null
  summary?: string | null
  completed_at?: string | null
  scored_at?: string | null
  proctoring: ProctoringInfo
  questions: ScorecardQuestion[]
  // Integrity events (fullscreen exits, auto-submission, and any camera/mic
  // detections) broken down by category, a total count, and a short
  // AI-generated narrative summary — see ScorecardResponse in
  // proctored_interviews.py. integrity_total is 0 / integrity_breakdown is
  // empty when the session had no integrity signals at all.
  integrity_breakdown: Record<string, number>
  integrity_total: number
  integrity_summary?: string | null
  // How much of the interview was actually answered. Scoring only averages
  // attempted questions, so on an interrupted session the overall score must
  // never be read without this denominator — a candidate who answered 2 of 10
  // well would otherwise look like a full pass.
  questions_answered: number
  questions_total: number
  // How many SKILLS a question actually covered. The overall score averages
  // only the assessed ones, so when these differ it rests on partial evidence.
  skills_assessed?: number | null
  skills_total?: number | null
  // The recruiter's decision on this candidate. Null until someone decides.
  review_status?: 'approved' | 'rejected' | null
  review_reason?: string | null
  reviewed_by_name?: string | null
  reviewed_at?: string | null
  // The candidate's own feedback on the experience, left on the thank-you
  // screen. Null when skipped, which is always allowed.
  candidate_feedback?: {
    rating: number
    // Stable keys, not display text — see CANDIDATE_FEEDBACK_LABELS.
    tags: string[]
    comment?: string | null
    submitted_at: string
  } | null
  // Code Plagiarism / Similarity Detection — every likely-copying match
  // involving this session, on either side of the comparison (see
  // check_plagiarism_for_session / ScorecardResponse.plagiarism_matches in
  // proctored_interviews.py). Empty when nothing was flagged.
  plagiarism_matches: PlagiarismMatch[]
}

export const getConfigForJob = async (jobId: string): Promise<ProctoredInterviewConfig | null> => {
  const response = await apiV2.get(`/proctored-interviews/jobs/${jobId}/config`)
  return response.data
}

export const createConfig = async (jobId: string): Promise<ProctoredInterviewConfig> => {
  const response = await apiV2.post(`/proctored-interviews/jobs/${jobId}/config`)
  return response.data
}

export const extractContext = async (configId: string): Promise<ProctoredInterviewConfig> => {
  const response = await apiV2.post(`/proctored-interviews/configs/${configId}/extract-context`)
  return response.data
}

export const updateConfig = async (
  configId: string,
  data: Partial<{ skills_rubrics: SkillRubric[] }>
): Promise<ProctoredInterviewConfig> => {
  const response = await apiV2.put(`/proctored-interviews/configs/${configId}`, data)
  return response.data
}

export const generateQuestions = async (
  configId: string
): Promise<{ config: ProctoredInterviewConfig; coverage_warnings: string[] }> => {
  const response = await apiV2.post(`/proctored-interviews/configs/${configId}/generate-questions`, {}, {
    timeout: 120000,
  })
  return response.data
}

export const createQuestion = async (
  configId: string,
  data: {
    skill_name: string
    question_type_id: string
    question_subtype_id?: string | null
    complexity_level_id?: string | null
    question_text: string
    guidance_notes?: string
    duration_minutes?: number
    rubric_criteria_covered?: string[]
    test_cases?: TestCase[]
  }
): Promise<ProctoredQuestion> => {
  const response = await apiV2.post(`/proctored-interviews/configs/${configId}/questions`, data)
  return response.data
}

export const updateQuestion = async (
  configId: string,
  questionId: string,
  data: Partial<{ question_text: string; guidance_notes: string; duration_minutes: number; rubric_criteria_covered: string[]; test_cases: TestCase[]; complexity_level_id: string | null }>
): Promise<ProctoredQuestion> => {
  const response = await apiV2.put(`/proctored-interviews/configs/${configId}/questions/${questionId}`, data)
  return response.data
}

export const deleteQuestion = async (configId: string, questionId: string): Promise<void> => {
  await apiV2.delete(`/proctored-interviews/configs/${configId}/questions/${questionId}`)
}

export const listSubmissions = async (jobId: string): Promise<SubmissionSession[]> => {
  const response = await apiV2.get(`/proctored-interviews/jobs/${jobId}/submissions`)
  return response.data
}

export const getScorecard = async (sessionId: string): Promise<Scorecard> => {
  const response = await apiV2.get(`/proctored-interviews/sessions/${sessionId}/scorecard`)
  return response.data
}

export const rescoreSession = async (sessionId: string): Promise<Scorecard> => {
  const response = await apiV2.post(`/proctored-interviews/sessions/${sessionId}/rescore`, {}, { timeout: 120000 })
  return response.data
}

// "Jane_Doe_<kind>_2026-10-07_14-05.pdf" — candidate name, report kind, and
// the local date/time of the download.
const reportFileName = (candidateName: string | null | undefined, kind: string): string => {
  const name = (candidateName || 'Candidate').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Candidate'
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${name}_${kind}_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}.pdf`
}

export const downloadScorecardPdf = async (sessionId: string, candidateName?: string | null): Promise<void> => {
  const response = await apiV2.get(`/proctored-interviews/sessions/${sessionId}/scorecard/pdf`, {
    responseType: 'blob',
  })
  const blob = response.data
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = reportFileName(candidateName, 'Proctored_Interview')
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export const getAuditLog = async (configId: string): Promise<AuditLogEntry[]> => {
  const response = await apiV2.get(`/proctored-interviews/configs/${configId}/audit-log`)
  return response.data
}

// Emails the candidate their proctored interview link (replaces the old
// "Copy Interview Link" clipboard action) using a dedicated, business-
// friendly HTML template — see backend EmailService.build_proctored_interview_invite_body.
export const sendCandidateEmail = async (sessionId: string): Promise<{ sent: boolean; message: string; sent_at: string }> => {
  const response = await apiV2.post(`/proctored-interviews/sessions/${sessionId}/send-email`)
  return response.data
}

// Issues a fresh interview link to one candidate whose previous attempt was
// interrupted. Creates a NEW session rather than resetting the old one, so
// the interrupted attempt keeps its own partial scorecard and recording —
// the superseded link needs no invalidation, because its own session is
// still 'incomplete' and keeps showing the Assessment Interrupted screen.
// Returns the new latest-attempt row for this candidate.
export const reissueSession = async (sessionId: string): Promise<SubmissionSession> => {
  const response = await apiV2.post(`/proctored-interviews/sessions/${sessionId}/reissue`)
  return response.data
}

// Follow-up phone/email shown to a candidate whose assessment was
// interrupted. Org-wide, stored on the organization's settings.
export interface SupportContact {
  phone?: string | null
  email?: string | null
}

export const getSupportContact = async (): Promise<SupportContact> => {
  const response = await apiV2.get('/proctored-interviews/settings/support-contact')
  return response.data
}

export const updateSupportContact = async (contact: SupportContact): Promise<SupportContact> => {
  const response = await apiV2.put('/proctored-interviews/settings/support-contact', contact)
  return response.data
}

// Records the recruiter's Approve/Reject decision on an assessment. A reason
// is required in both directions — an approval nobody can explain later is as
// awkward as an unexplained rejection. Returns the refreshed scorecard.
export const reviewSession = async (
  sessionId: string,
  status: 'approved' | 'rejected',
  reason: string,
): Promise<Scorecard> => {
  const response = await apiV2.post(`/proctored-interviews/sessions/${sessionId}/review`, { status, reason })
  return response.data
}
